import { useMutation } from '@apollo/client';
import _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import {
  CreateRedeemCodeMutation,
  CreateRedeemCodeMutationVariables,
  DeleteRedeemCodeMutation,
  DeleteRedeemCodeMutationVariables,
  RedeemCodeFragmentFragment,
  RedeemCodePlatform,
  UpdateRedeemCodeMutation,
  UpdateRedeemCodeMutationVariables,
  UserSubscriptionTier,
} from '../../../../../../__gqltypes__';
import { CREATE_REDEEM_CODE, DELETE_REDEEM_CODE, UPDATE_REDEEM_CODE } from '../graphql';
import { EditableReward, isValidRedeemCode, parseFilterUserIds, rewardUnionToInput } from '../utils';
import RedeemCodeDateField from './RedeemCodeDateField';
import RedeemCodeRewardsEditor from './RedeemCodeRewardsEditor';

type Props = {
  // null = create mode, otherwise the redeem code being edited
  redeemCode: RedeemCodeFragmentFragment | null;
  onSaved: (redeemCode: RedeemCodeFragmentFragment) => void;
  onCancel?: () => void;
  onDeleted?: (id: string) => void;
};

const defaultStartDate = () => moment().utc().set({ second: 0, millisecond: 0 }).toDate();
const defaultEndDate = () => moment().utc().set({ second: 0, millisecond: 0 }).add(7, 'days').toDate();

// First client version shipping the redeem code feature — shown as a hint, not prefilled
const MIN_SUPPORTED_APP_VERSION = '003.036.000';

const RedeemCodeForm = ({ redeemCode, onSaved, onCancel, onDeleted }: Props) => {
  const isCreateMode = redeemCode === null;

  const [code, setCode] = useState(redeemCode?.code ?? '');
  const [successMessage, setSuccessMessage] = useState(redeemCode?.successMessage ?? '');
  const [startDate, setStartDate] = useState<Date>(
    redeemCode ? new Date(redeemCode.startDate) : defaultStartDate()
  );
  const [noExpiry, setNoExpiry] = useState(redeemCode ? _.isNil(redeemCode.endDate) : false);
  const [endDate, setEndDate] = useState<Date>(
    redeemCode?.endDate ? new Date(redeemCode.endDate) : defaultEndDate()
  );
  const [filterUserIdsText, setFilterUserIdsText] = useState((redeemCode?.filterUserIds ?? []).join('\n'));
  const [filterAppVersion, setFilterAppVersion] = useState(redeemCode?.filterAppVersion ?? '');
  const [filterSubscriptionTier, setFilterSubscriptionTier] = useState<string>(
    redeemCode?.filterSubscriptionTier ?? ''
  );
  const [filterPlatform, setFilterPlatform] = useState<string>(redeemCode?.filterPlatform ?? '');
  const [rewards, setRewards] = useState<EditableReward[]>([]);

  const [createRedeemCode, { loading: createLoading }] = useMutation<
    CreateRedeemCodeMutation,
    CreateRedeemCodeMutationVariables
  >(CREATE_REDEEM_CODE);
  const [updateRedeemCode, { loading: updateLoading }] = useMutation<
    UpdateRedeemCodeMutation,
    UpdateRedeemCodeMutationVariables
  >(UPDATE_REDEEM_CODE);
  const [deleteRedeemCode, { loading: deleteLoading }] = useMutation<
    DeleteRedeemCodeMutation,
    DeleteRedeemCodeMutationVariables
  >(DELETE_REDEEM_CODE);

  const loading = createLoading || updateLoading || deleteLoading;

  // Reset the form whenever another redeem code is selected or create mode is entered
  useEffect(() => {
    setCode(redeemCode?.code ?? '');
    setSuccessMessage(redeemCode?.successMessage ?? '');
    setStartDate(redeemCode ? new Date(redeemCode.startDate) : defaultStartDate());
    setNoExpiry(redeemCode ? _.isNil(redeemCode.endDate) : false);
    setEndDate(redeemCode?.endDate ? new Date(redeemCode.endDate) : defaultEndDate());
    setFilterUserIdsText((redeemCode?.filterUserIds ?? []).join('\n'));
    setFilterAppVersion(redeemCode?.filterAppVersion ?? '');
    setFilterSubscriptionTier(redeemCode?.filterSubscriptionTier ?? '');
    setFilterPlatform(redeemCode?.filterPlatform ?? '');
    setRewards(
      (redeemCode?.rewards ?? [])
        .map(rewardUnionToInput)
        .filter((reward): reward is EditableReward => !_.isNil(reward))
    );
  }, [redeemCode]);

  const trimmedCode = code.trim();
  const codeIsValid = isValidRedeemCode(trimmedCode);
  const datesAreValid = noExpiry || startDate.getTime() < endDate.getTime();
  const canSave = codeIsValid && datesAreValid && rewards.length > 0 && !loading;

  const handleSave = () => {
    const commonVariables = {
      code: trimmedCode,
      successMessage: successMessage.trim() ? successMessage.trim() : null,
      startDate,
      endDate: noExpiry ? null : endDate,
      filterAppVersion: filterAppVersion.trim() ? filterAppVersion.trim() : null,
      filterSubscriptionTier: filterSubscriptionTier ? (filterSubscriptionTier as UserSubscriptionTier) : null,
      filterPlatform: filterPlatform ? (filterPlatform as RedeemCodePlatform) : null,
      rewards: rewards.map(({ type, amount, itemId }) => ({ type, amount, itemId })),
    };

    if (isCreateMode) {
      createRedeemCode({
        variables: { ...commonVariables, filterUserIds: parseFilterUserIds(filterUserIdsText) },
      })
        .then(({ data }) => {
          if (data?.createRedeemCode) {
            onSaved(data.createRedeemCode);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      updateRedeemCode({
        variables: {
          ...commonVariables,
          id: redeemCode.id,
          filterUserIds: parseFilterUserIds(filterUserIdsText) ?? [],
        },
      })
        .then(({ data }) => {
          if (data?.updateRedeemCode) {
            onSaved(data.updateRedeemCode);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };

  const handleDelete = () => {
    if (isCreateMode || !onDeleted) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete redeem code "${redeemCode.code}"? This cannot be undone.`)) {
      return;
    }
    deleteRedeemCode({ variables: { id: redeemCode.id } })
      .then(() => {
        onDeleted(redeemCode.id);
      })
      .catch((error) => {
        console.error(error);
        // eslint-disable-next-line no-alert
        alert(
          error?.message?.includes('REDEEM_CODE_HAS_REDEMPTIONS')
            ? 'This code cannot be deleted because players have already redeemed it. Set the expiry date to the past to disable it instead.'
            : `Failed to delete the redeem code: ${error?.message ?? 'unknown error'}`
        );
      });
  };

  return (
    <>
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Code</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. WELCOME2026"
              value={code}
              isInvalid={code.length > 0 && !codeIsValid}
              onChange={(e) => setCode(e.target.value)}
            />
            <Form.Control.Feedback type="invalid">
              Only uppercase letters A-Z and digits 0-9 are allowed (no spaces, lowercase or special characters).
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group>
            <Form.Label>Success message (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Shown in the 'Redeemed Successfully!' popup. Leave empty to use the default message."
              value={successMessage}
              onChange={(e) => setSuccessMessage(e.target.value)}
            />
          </Form.Group>

          <RedeemCodeDateField label="Start date" value={startDate} onChange={setStartDate} />
          <div
            className="rounded p-2 mb-3"
            style={
              noExpiry
                ? { backgroundColor: '#ffc107', border: '2px solid #d39e00' }
                : { backgroundColor: '#fff8e1', border: '2px solid #ffc107' }
            }
          >
            <Form.Check
              type="checkbox"
              id="redeem-code-no-expiry"
              label={
                <span className="font-weight-bold">
                  <i className={`fas fa-infinity mr-2 ${noExpiry ? 'text-dark' : 'text-warning'}`} />
                  No expiry (lifetime code)
                </span>
              }
              checked={noExpiry}
              onChange={(e) => setNoExpiry(e.target.checked)}
            />
            {noExpiry && (
              <Form.Text className="text-dark mb-0">
                This code never expires. To disable it later, uncheck this and set an expiry date in the past.
              </Form.Text>
            )}
          </div>
          {!noExpiry && <RedeemCodeDateField label="Expiry date" value={endDate} onChange={setEndDate} />}
          {!datesAreValid && <Form.Text className="text-danger">Start date must be before expiry date.</Form.Text>}
        </Col>

        <Col md={6}>
          <fieldset className="border rounded p-3 mb-3">
            <legend className="w-auto px-2 h6 mb-0">Matcher (leave empty for all players)</legend>

            <Form.Group>
              <Form.Label>User IDs</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Comma or newline separated user IDs."
                value={filterUserIdsText}
                onChange={(e) => setFilterUserIdsText(e.target.value)}
              />
            </Form.Group>

            <Form.Group>
              <Form.Label>Minimum app version</Form.Label>
              <Form.Control
                type="text"
                placeholder="Leave empty for all versions"
                value={filterAppVersion}
                onChange={(e) => setFilterAppVersion(e.target.value)}
              />
              <Form.Text className="text-muted">
                Leave empty to allow all versions. Note: the first client version supporting redeem codes is{' '}
                {MIN_SUPPORTED_APP_VERSION} — older clients have no redeem code menu.
              </Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Minimum subscription tier</Form.Label>
              <Form.Control
                as="select"
                value={filterSubscriptionTier}
                onChange={(e) => setFilterSubscriptionTier(e.target.value)}
              >
                <option value="">Any (no subscription required)</option>
                {Object.values(UserSubscriptionTier).map((tier) => (
                  <option key={tier} value={tier}>
                    {tier}
                  </option>
                ))}
              </Form.Control>
              <Form.Text className="text-muted">Players with this tier or higher are eligible.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Platform</Form.Label>
              <Form.Control as="select" value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)}>
                <option value="">All platforms</option>
                <option value={RedeemCodePlatform.IOS}>iOS</option>
                <option value={RedeemCodePlatform.ANDROID}>Android</option>
              </Form.Control>
            </Form.Group>
          </fieldset>
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={{ span: 8, offset: 2 }}>
          <RedeemCodeRewardsEditor rewards={rewards} onChange={setRewards} centered />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col className="d-flex align-items-center justify-content-end">
          {!isCreateMode && onDeleted && (
            <Button variant="outline-danger" className="mr-auto" disabled={loading} onClick={handleDelete}>
              <i className="fas fa-trash-alt mr-2" />
              Delete
            </Button>
          )}
          {!isCreateMode && redeemCode.endDate && moment(redeemCode.endDate).isBefore(moment()) && (
            <span className="text-muted mr-3">
              This code has expired. Players can no longer redeem it; there is no delete/disable control.
            </span>
          )}
          {isCreateMode && onCancel && (
            <Button variant="outline-secondary" className="mr-2" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant={isCreateMode ? 'success' : 'primary'} disabled={!canSave} onClick={handleSave}>
            {loading ? <Spinner animation="border" size="sm" /> : isCreateMode ? 'Create' : 'Save'}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default RedeemCodeForm;
